import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { applyLook, DEFAULT_LOOK, galleryUrl, resetLook, themeOf } from "@/lib/themes";
import { blobUrl, clearBlobCache } from "@/lib/authedImage";

const LookCtx = createContext(null);
export const useLook = () => useContext(LookCtx);
export const SAVE_FAIL = "Couldn't save your look. We'll try again.";

const personKey = (s) => (!s ? null : s.type === "kid" ? `kid:${s.kid.id}` : `user:${s.user.id}`);
const pendingKey = (k) => `qhh.lookPending.${k}`;
const savable = (l) => ({ theme: l.theme, wallpaper: { type: l.wallpaper.type, id: l.wallpaper.id }, dim: l.dim, screensaver: l.screensaver, sound_muted: !!l.sound_muted });

export function LookProvider({ children }) {
  const { session } = useAuth();
  const key = personKey(session);
  const [look, setLookState] = useState(null);
  const [mineUrl, setMineUrl] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [screensaver, setScreensaver] = useState({ on: false, since: 0 });
  const [pin, setPin] = useState(null);
  const lookRef = useRef(null);
  const timer = useRef(null);

  const setLook = (l) => { lookRef.current = l; setLookState(l); };

  const persist = useCallback(async (k) => {
    const l = lookRef.current;
    try {
      await api.put("/me/look", savable(l));
      localStorage.removeItem(pendingKey(k));
      setSaveError(false);
    } catch (_) {
      localStorage.setItem(pendingKey(k), JSON.stringify(l));
      setSaveError(true);
      toast.error(SAVE_FAIL, { id: "look-save-fail" });
    }
  }, []);

  // New person signed in: use their saved look, or a look that failed to save earlier on this device.
  useEffect(() => {
    clearBlobCache();
    setMineUrl(null);
    setSaveError(false);
    setScreensaver({ on: false, since: 0 });
    if (!key) { setLook(null); return; }
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem(pendingKey(key)) || "null"); } catch (_) { /* ignore */ }
    setLook({ ...DEFAULT_LOOK, ...(session.look || {}), ...(pending || {}) });
    if (pending) persist(key);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const wp = look?.wallpaper;
  useEffect(() => {
    if (wp?.type !== "mine") { setMineUrl(null); return; }
    let live = true;
    blobUrl(`/me/wallpapers/${wp.id}/file`).then((u) => live && setMineUrl(u)).catch(() => live && setMineUrl(null));
    return () => { live = false; };
  }, [wp?.type, wp?.id]);

  // Public pages pin today's dark look; the sitter guest page pins Cream. Otherwise the person's look, or dark when signed out.
  useLayoutEffect(() => {
    if (pin === "dark") resetLook({ keepCache: !!key });
    else if (pin) applyLook({ ...DEFAULT_LOOK, theme: pin }, null, { cache: false });
    else if (look) applyLook(look, mineUrl);
    else if (!key) resetLook();
  }, [look, mineUrl, pin, key]);

  // Apply at once; save now, or after a short pause for sliders.
  const update = useCallback((patch, { debounce = false } = {}) => {
    const next = { ...lookRef.current, ...patch };
    setLook(next);
    clearTimeout(timer.current);
    if (debounce) timer.current = setTimeout(() => persist(key), 450);
    else persist(key);
  }, [key, persist]);

  // Server already saved (custom themes, wallpaper delete): take its look, keep any unsaved local fields.
  const replace = useCallback((serverLook) => setLook({ ...DEFAULT_LOOK, ...serverLook }), []);

  const wallpaperUrl = wp?.type === "gallery" ? galleryUrl(wp.id) : wp?.type === "mine" ? mineUrl : null;
  const startScreensaver = useCallback(() => setScreensaver({ on: true, since: Date.now() }), []);
  const stopScreensaver = useCallback(() => setScreensaver({ on: false, since: 0 }), []);

  return (
    <LookCtx.Provider value={{ look, update, replace, saveError, wallpaperUrl, theme: pin ? themeOf({ theme: pin }) : look ? themeOf(look) : themeOf({ theme: "dark" }),
      screensaver, startScreensaver, stopScreensaver, setPin }}>
      {children}
    </LookCtx.Provider>
  );
}

export function usePinTheme(id) {
  const { setPin } = useContext(LookCtx);
  useLayoutEffect(() => { setPin(id); return () => setPin(null); }, [id, setPin]);
}

export function PinDark({ children }) {
  usePinTheme("dark");
  return children;
}
